import { useQueryClient } from "@tanstack/react-query";
import { array, bool, number, string } from "prop-types";
import { forwardRef, useState } from "react";

import styled from "styled-components/macro";
import { themeVariants } from "theme";
import padding from "theme/interpolations/padding";

import FnrTypography from "UI/FnrTypography";
import { twoFALoginKeys } from "Login/TwoFA/API/keys";
import { use2FAState } from "Login/TwoFA/API/queryHooks";
import { useTwoFAContext } from "Login/TwoFA/TwoFA";

import FnrButtonWrapper from "UI/FnrButtonWrapper";
import FnrCard from "UI/FnrCard";
import FnrContainedIcon from "UI/FnrContainedIcon";
import FnrDivider from "UI/FnrDivider";
import FnrNetworkPhoto from "UI/FnrNetworkPhoto";
import FnrScrollNFade from "UI/FnrScrollNFade";
import FnrTappyShrink from "UI/FnrTappyShrink";
import FnrHorizontalGap from "UI/commonContainers/FnrHorizontalGap";
import FnrVerticalGap from "UI/commonContainers/FnrVerticalGap";

import useLastOrDefault from "Utilities/Hooks/useLastOrDefault";
import truncate from "Utilities/truncate";

import MissingNetworksLink from "../../MissingNetworksLink";
import PromptTypography from "../../PromptTypography";
import StartOver from "../../StartOver";

const SFnrButtonWrapper = styled(FnrButtonWrapper)`
  ${padding}
  width: 100%;
  min-height: 58px;
  z-index: 1;
  transition: background-color 1200ms;
  &:hover,
  &:focus {
    background-color: ${({
      theme: {
        palette: { taupe },
      },
    }) => `${taupe}1A`};
    transition: background-color 200ms;
  }
`;

const SFnrTappyShrink = styled(FnrTappyShrink)`
  display: block;
`;

const Network = forwardRef(
  ({ id, name, colorScheme, photoUrl, addDivider }, ref) => {
    const [{ isLoading, ...twoFAData }, refetch] = use2FAState();
    const [mayShowLoadState, setMayShowLoadState] = useState(false);
    const queryClient = useQueryClient();
    const { setNetworkID, firstFactor, variant } = useTwoFAContext();

    const handleSetNetworkID = () => {
      setMayShowLoadState(true);
      setNetworkID(id);
      const optimisticObject = {
        ...twoFAData,
        networkPhotoUrl: photoUrl,
        networkName: name,
        colorScheme,
      };
      if (firstFactor) optimisticObject.networkID = id;
      queryClient.setQueryData(twoFALoginKeys[variant](), optimisticObject);
      // setTimeout allows state to be set before refetch happens.
      setTimeout(refetch);
    };

    return (
      <>
        <SFnrButtonWrapper
          onClick={handleSetNetworkID}
          aria-label={`log in to ${name}`}
          $variant={themeVariants.outerContainer}
          ref={ref}
        >
          <SFnrTappyShrink>
            <FnrHorizontalGap fullWidth>
              <FnrHorizontalGap>
                <FnrNetworkPhoto size="xl" src={photoUrl} shape="circle" />
                <FnrTypography fontSize="xl" type="subheader" color="textBlack">
                  {truncate(name, { length: 40 })}
                </FnrTypography>
              </FnrHorizontalGap>
              <FnrContainedIcon
                icon="fa-chevron-right fas"
                color="taupe"
                isLoading={mayShowLoadState && isLoading}
              />
            </FnrHorizontalGap>
          </SFnrTappyShrink>
        </SFnrButtonWrapper>
        {addDivider && <FnrDivider />}
      </>
    );
  }
);

Network.propTypes = {
  id: number,
  name: string,
  colorScheme: array,
  photoUrl: string,
  addDivider: bool,
};

const NetworkSelect = () => {
  const [{ networks: networksArg, userHasAdditionalNetworks }] = use2FAState();
  const networks = useLastOrDefault(networksArg, []);

  return (
    <FnrVerticalGap data-cy="TwoFA_NetworkSelect">
      <PromptTypography>Select a network to log into.</PromptTypography>
      <FnrScrollNFade scrollbar fullWidth>
        <FnrCard variant="modal" clip>
          <FnrVerticalGap gap={0}>
            {networks.map(({ ID, name, colorScheme, photoUrl }, idx) => {
              const isLast = idx === networks.length - 1;
              return (
                <Network
                  key={ID}
                  id={ID}
                  name={name}
                  colorScheme={colorScheme}
                  photoUrl={photoUrl}
                  addDivider={!isLast}
                />
              );
            })}
          </FnrVerticalGap>
        </FnrCard>
      </FnrScrollNFade>
      {userHasAdditionalNetworks ? <MissingNetworksLink /> : <StartOver />}
    </FnrVerticalGap>
  );
};

export default NetworkSelect;
